import React, { useCallback, useMemo, useState } from 'react';
import {
  SalvButton,
  SalvModal,
  SalvProgressCircle,
  SalvTable,
  SalvTablePaginationType,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { InternalApiCase, InternalApiCaseAttachmentType, InternalApiLogicalOperator } from '../../api-internal';
import { useAttachToCases, useQueryCaseHandles, useQueryCasesPage } from '../casesQueryActions';
import { formatDate } from '../../common/datetime/FormattedDateTime';
import { createColumnHelper, PaginationState, Row } from '@tanstack/react-table';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { ATTACHMENT_TYPE_LABEL } from './AttachmentCases';

interface AttachToCasesModalProps {
  attachmentType: InternalApiCaseAttachmentType;
  externalId: string;
  onClose: () => void;
}

const PAGE_SIZE = 20;

const AttachToCasesModal = ({ attachmentType, externalId, onClose }: AttachToCasesModalProps) => {
  const entityLabel = ATTACHMENT_TYPE_LABEL[attachmentType];
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const {
    data: casesPage,
    isPending: isCasesPending,
    isError: isCasesError,
  } = useQueryCasesPage({ filters: [], logicalOperator: InternalApiLogicalOperator.AND, page });
  const {
    data: attachedHandles,
    isPending: isHandlesPending,
    isError: isHandlesError,
  } = useQueryCaseHandles(attachmentType, externalId);
  const attachToCases = useAttachToCases(attachmentType);
  const [selectedRows, setSelectedRows] = useState<InternalApiCase[]>([]);

  const attachedHandlesSet = useMemo(() => new Set(attachedHandles ?? []), [attachedHandles]);

  const canAttachToCase = useMemo(() => {
    return (row: Row<InternalApiCase>) => !attachedHandlesSet.has(row.original.handle);
  }, [attachedHandlesSet]);

  const setPagination = useCallback((newPagination: PaginationState) => {
    setPage(newPagination.pageIndex);
    setSelectedRows([]);
  }, []);

  const columnHelper = createColumnHelper<InternalApiCase>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('details.name', {
        header: 'Name',
      }),
      columnHelper.accessor('details.description', {
        header: 'Description',
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        cell: ({ getValue, row }) => (
          <SalvTooltipWrapper
            tooltip={`${row.original.createdBy.name}, ${formatDate(getValue(), true)}`}
            placement='top'
          >
            {formatDate(getValue(), true)}
          </SalvTooltipWrapper>
        ),
      }),
    ],
    []
  );

  const handleConfirm = async () => {
    try {
      await attachToCases.mutateAsync({
        caseHandles: selectedRows.map((r) => r.handle),
        ids: [externalId],
      });
      dispatch(showFeedback({ message: `${entityLabel} attached to Cases successfully`, success: true }));
      onClose();
    } catch {
      dispatch(showFeedback({ message: `Failed to attach ${entityLabel} to Cases`, success: false }));
    }
  };

  const isPending = isCasesPending || isHandlesPending;
  const isError = isCasesError || isHandlesError;

  return (
    <SalvModal show onHide={onClose}>
      <SalvModal.Header>Attach to Case</SalvModal.Header>
      <SalvModal.Body>
        {isPending ? (
          <SalvProgressCircle ariaLabel='Loading Cases' />
        ) : isError ? (
          <DataLoadingErrorFallback message='Failed to load Cases' />
        ) : (
          <SalvTable
            columns={columns}
            data={casesPage?.content ?? []}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            isRowSelectionEnabled={canAttachToCase}
            emptyMessage='No Cases available'
            pagination={{
              type: SalvTablePaginationType.PAGINATION,
              total: casesPage?.total ?? 0,
              state: { pageIndex: page, pageSize: PAGE_SIZE },
              setPagination,
            }}
          />
        )}
      </SalvModal.Body>
      <SalvModal.Footer>
        <SalvButton label='Cancel' variant='muted' size={Size.MEDIUM} onClick={onClose} />
        <SalvButton
          label='Confirm'
          variant='primary'
          size={Size.MEDIUM}
          disabled={selectedRows.length === 0 || attachToCases.isPending}
          onClick={handleConfirm}
        />
      </SalvModal.Footer>
    </SalvModal>
  );
};

export default AttachToCasesModal;
